import type { CleanRoomsContent } from '@/content/home';
import { ArrowRight, Check, FileText, Lock, LockOpen } from '@/components/ui/Icons';

export type CloakedCleanRoomsTeaserProps = {
  content: CleanRoomsContent;
};

export function CloakedCleanRoomsTeaser({ content }: CloakedCleanRoomsTeaserProps) {
  return (
    <section
      id="clean-rooms"
      aria-labelledby="clean-rooms-heading"
      className="bg-canvas px-6 py-[120px] md:px-[72px]"
    >
      <div className="mx-auto flex max-w-[1296px] flex-col items-start justify-between gap-12 lg:flex-row lg:gap-20">
        <div className="flex w-full flex-col gap-8 lg:max-w-[636px]">
          <span className="inline-flex items-center gap-2 self-start rounded-full border border-cloak-accent/20 bg-page px-3.5 py-1.5">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-cloak-accent" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cloak-accent">
              {content.tag}
            </span>
          </span>

          <h2
            id="clean-rooms-heading"
            className="text-[32px] font-bold leading-[1.15] tracking-[-1px] text-ink md:text-[42px]"
          >
            {content.heading}
          </h2>

          <p className="text-[16px] leading-[1.55] text-graphite md:text-[17px]">{content.body}</p>

          <ul className="flex flex-col gap-3.5">
            {content.bullets.map((bullet, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cloak-tint text-cloak-accent"
                >
                  <Check size={12} />
                </span>
                <span className="text-[15px] leading-[1.5] text-ink md:text-[16px]">{bullet}</span>
              </li>
            ))}
          </ul>

          <a
            href="mailto:hello@cloakling.com?subject=Cloaked%20Clean%20Rooms%20design%20partner"
            className="inline-flex items-center gap-2 self-start text-[15px] font-semibold text-cloak-accent no-underline hover:text-cloak-accent/80"
          >
            {content.link}
            <ArrowRight size={16} />
          </a>
        </div>

        <CleanRoomsCard />
      </div>
    </section>
  );
}

function CleanRoomsCard() {
  return (
    <div
      aria-hidden="true"
      className="flex w-full max-w-[560px] flex-col gap-5 rounded-[18px] border border-rule bg-page p-8 shadow-[0_18px_48px_rgba(20,20,40,0.06)]"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock size={14} className="text-cloak-accent" />
          <span className="text-[13px] font-semibold text-ink">Hawthorne Family Practice</span>
        </div>
        <span className="text-[11px] font-medium text-pencil">3 documents · 5 staff</span>
      </div>

      <div className="flex flex-col gap-2.5 rounded-[10px] border border-rule bg-canvas p-4">
        {[
          'patient-records-march.pdf',
          'insurance-prior-auth.pdf',
          'billing-summary-q1.pdf',
        ].map((file) => (
          <div
            key={file}
            className="flex items-center gap-2.5 rounded-md border border-rule bg-page px-3 py-2.5"
          >
            <FileText size={16} className="text-cloak-accent" />
            <span className="flex-1 text-[12px] font-medium text-ink">{file}</span>
            <span className="rounded-sm bg-cloak-tint px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cloak-accent">
              CLOAKED
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-pencil">
          PERMISSIONED ACCESS
        </p>
        {[
          { initials: 'PO', name: 'Practice Owner', sub: 'Owner · sees full originals', avatar: '#FDE8D8', open: true },
          { initials: 'OM', name: 'Office Manager', sub: 'Manager · sees cloaked copy', avatar: '#E8E0F4', open: false },
          { initials: 'BV', name: 'Billing Vendor', sub: 'Vendor · cloaked access only', avatar: '#DBEAFE', open: false },
        ].map((member) => (
          <div
            key={member.initials}
            className="flex items-center gap-2.5 rounded-lg border border-rule bg-canvas px-3 py-2.5"
          >
            <span
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-ink"
              style={{ background: member.avatar }}
            >
              {member.initials}
            </span>
            <div className="flex flex-1 flex-col">
              <span className="text-[12px] font-semibold text-ink">{member.name}</span>
              <span className="text-[10px] text-pencil">{member.sub}</span>
            </div>
            {member.open ? (
              <LockOpen size={14} className="text-cloak-accent" />
            ) : (
              <Lock size={14} className="text-cloak-accent" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
